import { characterCount } from '../../lib/copy';
import { NOTE_LIMIT, overBy, type ProseLimit } from '../../lib/prose-limit';

/**
 * **The live count under a capped prose field**, in the one shape all of them
 * share: what has been written, over what is allowed, at the end of the field it
 * counts. The import module's two counts — the new-import description and the
 * chat composer — still render their own markup against their own caps, and are
 * deliberately left where they are.
 *
 * It has two states and no third. At or under the cap it is a fact in the page's
 * faintest ink; past the cap it is red. There is deliberately no approaching
 * shade in between — a field with room left is not a problem, and colouring it
 * as one asks the office to answer something that is not wrong yet.
 *
 * Written first inline on Settings (2026-09-02) and lifted here when the prose
 * notes were capped (2026-09-13), so that a count under an intake box and a
 * count under the club profile are the same object rather than two spellings of
 * one. `length` is the caller's, not the raw value's: every caller measures the
 * string it is going to send, which is the trimmed one.
 */
export function CharCount({ id, length, cap }: { id?: string; length: number; cap: number }) {
  return (
    <div id={id} className={length > cap ? 'char-count over' : 'char-count'}>
      {characterCount(length, cap)}
    </div>
  );
}

/**
 * **A capped box's whole signal**: the count, and — only past the cap — the line
 * saying what to do about it.
 *
 * The two ids it mints are what the textarea points `aria-describedby` at;
 * {@link noteDescribedBy} spells the same pair, so the box and the lines under
 * it cannot come apart.
 *
 * **`limit` carries the cap and its sentence together** (split-giant-note note,
 * 2026-09-14). It used to take neither: every prose box on the page was held to
 * one number and told to split into separate notes. That stopped being true when
 * the splitter went in behind the person boxes and not behind an organization's
 * statement — so the two now differ in *both* the number and the instruction,
 * and passing them as one object is what stops a box getting one of them from
 * the other. {@link NOTE_LIMIT} is the default because it is what most boxes are.
 *
 * Neither line is a live region, and for the reason the Settings count is not:
 * a count that announced itself on every keystroke would be unusable to exactly
 * the reader it was added for.
 */
export function NoteLength({
  id,
  value,
  limit = NOTE_LIMIT,
}: {
  id: string;
  value: string;
  limit?: ProseLimit;
}) {
  const over = overBy(value, limit.cap);

  return (
    <>
      <CharCount id={`${id}-count`} length={value.trim().length} cap={limit.cap} />
      {over > 0 ? (
        <div id={`${id}-over`} className="field-note over-note">
          {limit.overLine}
        </div>
      ) : null}
    </>
  );
}

/**
 * What the box says describes it: its count always, and the over-line as well
 * once there is one. Red text and a button gone quiet are the whole signal
 * otherwise, and neither reaches a reader who is not looking at the colour.
 *
 * It takes the same `limit` its box does, for the same reason: a box held to
 * 2,000 whose description was computed against 20,000 would stop naming the
 * over-line at exactly the length it appears.
 */
export function noteDescribedBy(id: string, value: string, limit: ProseLimit = NOTE_LIMIT): string {
  return overBy(value, limit.cap) > 0 ? `${id}-count ${id}-over` : `${id}-count`;
}
